import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCcw } from "lucide-react";

function fmtCents(cents) {
  const n = Number(cents || 0);
  return `$${(n / 100).toFixed(4)}`;
}
function fmtInt(n) {
  return Number(n || 0).toLocaleString();
}

export default function SuperadminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !stats) {
    return (
      <div
        className="font-sans text-sm text-[#7A7571] py-12"
        data-testid="superadmin-overview-loading"
      >
        Loading overview…
      </div>
    );
  }
  if (!stats) return null;

  return (
    <div data-testid="superadmin-overview">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-[#2C2A29]">
            Platform overview
          </h2>
          <p className="font-sans text-sm text-[#7A7571] mt-1">
            Last updated{" "}
            {stats.updated_at
              ? new Date(stats.updated_at).toLocaleString()
              : "—"}
          </p>
        </div>
        <button
          onClick={load}
          data-testid="superadmin-overview-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md font-sans text-sm text-[#2C2A29] border border-[#DCD7CE] bg-white hover:bg-[#EFEAE0] transition-colors"
        >
          <RefreshCcw size={13} strokeWidth={2} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total users"
          value={fmtInt(stats.total_users)}
          testid="stat-users"
        />
        <StatCard
          label="Total workspaces"
          value={fmtInt(stats.total_workspaces)}
          testid="stat-workspaces"
        />
        <StatCard
          label="Suspended workspaces"
          value={fmtInt(stats.total_workspaces_suspended)}
          accent={stats.total_workspaces_suspended > 0 ? "danger" : undefined}
          testid="stat-suspended"
        />
        <StatCard
          label="Lifetime raw cost"
          value={fmtCents(stats.lifetime_raw_cost_cents)}
          sub={`~${fmtCents(stats.lifetime_customer_cost_cents_approx)} billed`}
          testid="stat-cost"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Panel title="Workspaces by plan" testid="panel-by-plan">
          <div className="space-y-2">
            {Object.entries(stats.total_workspaces_by_plan || {}).map(
              ([plan, count]) => (
                <div
                  key={plan}
                  className="flex items-center justify-between font-sans text-sm"
                  data-testid={`plan-row-${plan}`}
                >
                  <span className="text-[#2C2A29]">{plan}</span>
                  <span className="font-mono text-[#7A7571]">
                    {fmtInt(count)}
                  </span>
                </div>
              )
            )}
          </div>
        </Panel>

        <Panel title="Last 24 hours" testid="panel-last-24h">
          <Row
            label="Assistant replies"
            value={fmtInt(stats.last_24h?.messages)}
            testid="last24-msgs"
          />
          <Row
            label="Input tokens"
            value={fmtInt(stats.last_24h?.input_tokens)}
            testid="last24-in"
          />
          <Row
            label="Output tokens"
            value={fmtInt(stats.last_24h?.output_tokens)}
            testid="last24-out"
          />
          <Row
            label="Raw cost"
            value={fmtCents(stats.last_24h?.raw_cost_cents)}
            testid="last24-cost"
          />
        </Panel>
      </div>

      <Panel title="Lifetime token totals" testid="panel-lifetime">
        <Row
          label="Input tokens"
          value={fmtInt(stats.lifetime_input_tokens)}
          testid="lifetime-in"
        />
        <Row
          label="Output tokens"
          value={fmtInt(stats.lifetime_output_tokens)}
          testid="lifetime-out"
        />
      </Panel>
    </div>
  );
}

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[#DCD7CE] bg-white p-5"
    >
      <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-2">
        {label}
      </p>
      <p
        className={`font-serif text-3xl tracking-tight ${
          accent === "danger" ? "text-[#8C3A1F]" : "text-[#2C2A29]"
        }`}
      >
        {value}
      </p>
      {sub ? (
        <p className="font-sans text-xs text-[#7A7571] mt-1">{sub}</p>
      ) : null}
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <section
      className="rounded-xl border border-[#DCD7CE] bg-white p-5"
      data-testid={testid}
    >
      <h3 className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A7571] font-semibold mb-3">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ label, value, testid }) {
  return (
    <div
      className="flex items-center justify-between font-sans text-sm"
      data-testid={testid}
    >
      <span className="text-[#2C2A29]">{label}</span>
      <span className="font-mono text-[#7A7571]">{value}</span>
    </div>
  );
}
